import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Building2, MapPin, Calendar, Users, DollarSign, Eye, Edit, Briefcase } from 'lucide-react';
import api, { getAssetUrl } from '@/utils/api';

const PrimeEmployerJobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [primeEmployers, setPrimeEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployer, setSelectedEmployer] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchJobs();
    fetchPrimeEmployers();
  }, [selectedEmployer, page]);

  const fetchPrimeEmployers = async () => {
    try {
      const response = await api.get('/admin/prime-employers-list');
      setPrimeEmployers(response.data || []);
    } catch (error) {
      console.error('Error fetching prime employers:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (selectedEmployer && selectedEmployer !== 'all') {
        params.append('prime_employer_id', selectedEmployer);
      }
      
      const response = await api.get(`/admin/prime-employer-jobs?${params}`);
      setJobs(response.data.jobs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  // Using getAssetUrl for images

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Prime Employer Jobs</h1>
          <p className="text-slate-600 text-sm mt-1">Jobs posted by admin/executives for Prime Employers</p>
        </div>
        <Button 
          onClick={() => navigate('/admin/post-job')}
          className="bg-teal-600 hover:bg-teal-700"
          data-testid="post-new-job-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Post New Job
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6 border-slate-200">
        <div className="flex gap-4">
          <div className="w-64">
            <Select value={selectedEmployer} onValueChange={setSelectedEmployer}>
              <SelectTrigger data-testid="filter-employer">
                <SelectValue placeholder="Filter by Prime Employer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Prime Employers</SelectItem>
                {primeEmployers.map((employer) => (
                  <SelectItem key={employer.id} value={employer.id}>
                    {employer.employer_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-slate-600 flex items-center">
            Total: <span className="font-semibold ml-1">{total}</span> jobs
          </div>
        </div>
      </Card>

      {/* Jobs List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      ) : jobs.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-600 mb-4">No jobs found</p>
          <Button 
            onClick={() => navigate('/admin/post-job')}
            className="bg-teal-600 hover:bg-teal-700"
          >
            Post Your First Job
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <Card key={job.id} className="p-4 border-slate-200 hover:border-teal-200 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex gap-4">
                  {/* Company Logo */}
                  <div className="w-14 h-14 rounded-lg border border-slate-200 overflow-hidden flex items-center justify-center bg-slate-50">
                    {job.company_logo ? (
                      <img 
                        src={getAssetUrl(job.company_logo)} 
                        alt={job.company_name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Building2 className="w-6 h-6 text-slate-300" />
                    )}
                  </div>
                  
                  {/* Job Info */}
                  <div>
                    <h3 className="font-semibold text-slate-900 text-lg">{job.job_title}</h3>
                    <p className="text-teal-600 font-medium">{job.company_name}</p>
                    
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {job.locations?.map(l => l.city || l.district).join(', ') || 'Multiple Locations'}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        ₹{job.min_salary?.toLocaleString()} - ₹{job.max_salary?.toLocaleString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {job.vacancies} vacancies
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Last date: {new Date(job.last_date).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <div className="flex gap-2 mt-3">
                      <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs font-medium">
                        {job.job_type}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${job.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}`}>
                        {job.status}
                      </span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                        Prime Employer
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`/jobs/${job.id}`, '_blank')}
                    data-testid={`view-job-${job.id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div className="flex justify-center gap-2 mt-6">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </Button>
          <span className="px-4 py-2 text-sm text-slate-600">
            Page {page} of {Math.ceil(total / 20)}
          </span>
          <Button
            variant="outline"
            disabled={page >= Math.ceil(total / 20)}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default PrimeEmployerJobs;
